import React from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';

import View from 'components/view';
import ViewLoading from 'components/view/loading';
import Modal from 'components/modal';
import Icon from 'components/icon';
import Button from 'components/button';
import { Field, Form } from 'components/form';
import Confetti from 'components/loading/confetti';

import StorefrontListItem from './list-item';
import StorefrontListItemCard from './list-item-card';
import StorefrontNewBuildYourOwn from './new-build-your-own';
import StorefrontNewFindAPartner from './new-find-a-partner';
import StorefrontNewAppView from './new-app-view';

import { locationWithQuery } from 'utils';
import { renderWithStorefrontLimitTooltip } from 'utils/storefront';
import {
  APP_STOREFRONT_USE_CASES,
  APP_STOREFRONT_PURCHASE_OPTIONS,
  APP_STOREFRONT_FEATURES,
} from 'constants/app';

import './storefront.scss';

const CATALOG_FILTERS = [
  {
    name: 'use_cases',
    label: 'Use cases',
    options: APP_STOREFRONT_USE_CASES,
  },
  {
    name: 'purchase_options',
    label: 'Selling options',
    options: APP_STOREFRONT_PURCHASE_OPTIONS,
  },
  {
    name: 'features',
    label: 'Features',
    options: APP_STOREFRONT_FEATURES,
  },
];

export const DEFAULT_CATALOG_FILTERS = {
  use_cases: [],
  purchase_options: [],
  features: [],
};

export default class StorefrontNew extends React.PureComponent {
  static contextTypes = {
    client: PropTypes.object.isRequired,
    openModal: PropTypes.func.isRequired,
  };

  static propTypes = {
    apps: PropTypes.object.isRequired,
    router: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,

    fetchAppList: PropTypes.func.isRequired,
    onCreateStorefront: PropTypes.func.isRequired,
    onCreateStorefrontWithApp: PropTypes.func.isRequired,
    onUploadTheme: PropTypes.func.isRequired,
  };

  state = {
    loading: false,
    loaded: false,
    creating: false,
    createApp: null,
    createError: false,
    showCreatingStorefront: false,
    filters: DEFAULT_CATALOG_FILTERS,
  };

  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      filters: props.location.query.filters || DEFAULT_CATALOG_FILTERS,
    };
  }

  componentDidMount() {
    const { client } = this.context;
    const { router } = this.props;

    if (!client.features.has('storefront_v2')) {
      router.replace('/storefronts/new/custom');
      return;
    }

    this.fetchAppList();
  }

  componentDidUpdate(prevProps) {
    const {
      router,
      location: { query },
    } = this.props;
    const { loaded, filters, hasOwnApps } = this.state;

    if (!loaded) {
      return;
    }

    const wasBrowseView = prevProps.location.query.view === 'browse';
    const isBrowseView = query.view === 'browse';
    const wasLibraryView = prevProps.location.query.view === 'library';
    const isLibraryView = query.view === 'library';
    const loadedLibraryView = isLibraryView && !wasLibraryView && !hasOwnApps;
    const switchedViews = query.view !== prevProps.location.query.view;
    const reloadedApps =
      prevProps.apps !== this.props.apps ||
      prevProps.installedApps !== this.props.installedApps;

    const nextFilters = query.filters || DEFAULT_CATALOG_FILTERS;
    if (!wasBrowseView && isBrowseView) {
      const isDefaultFilterState = isEqual(filters, DEFAULT_CATALOG_FILTERS);
      if (!isDefaultFilterState) {
        router.replace(
          locationWithQuery(this.props.location, { filters }, true),
        );
      }
    } else if (
      wasBrowseView &&
      isBrowseView &&
      !isEqual(filters, nextFilters)
    ) {
      this.setState({ filters: nextFilters }, () => {
        this.fetchAppList();
      });
    } else if (loadedLibraryView) {
      this.fetchAppList();
    }

    if (switchedViews || reloadedApps) {
      this.setState(this.getInitialState(this.props.apps, this.props));
    }
  }

  getInitialState(apps, props) {
    const { router, location } = props;
    const {
      query: { view },
    } = location;

    const items = apps.results;
    const libraryItems = items.filter(this.isLibraryAppAvailable);
    const catalogItems = items.filter(this.isCatalogApp);

    const hasOwnApps = libraryItems.length > 0;
    const isBrowseView = (!view && !hasOwnApps) || view === 'browse';
    const isBrowseOrLibraryView = isBrowseView || view === 'library';

    if (view === 'library' && !hasOwnApps) {
      router.replace(locationWithQuery(location, { view: 'browse' }, true));
    }

    const catalogFilterFields = this.getCatalogFilterFields(catalogItems);

    return {
      items,
      libraryItems,
      catalogItems,
      catalogFilterFields,
      hasOwnApps,
      isBrowseView,
      isBrowseOrLibraryView,
    };
  }

  fetchAppList = async () => {
    const { fetchAppList } = this.props;
    const { filters } = this.state;

    this.setState({ loading: true });

    const apps = await fetchAppList(filters);

    this.setState({
      ...this.getInitialState(apps, this.props),
      loading: false,
      loaded: true,
    });
  };

  onViewApp = (app) => {
    const { router, location } = this.props;
    router.push(
      locationWithQuery(
        location,
        { app: app.id /*, view: undefined, filters: undefined*/ },
        true,
      ),
    );
  };

  createStorefront = async (app, values) => {
    const { onCreateStorefrontWithApp } = this.props;

    this.setState({
      showCreatingStorefront: true,
      creating: true,
    });

    const storefrontCreated = await onCreateStorefrontWithApp(app, values);
    if (!storefrontCreated) {
      this.setState({ createError: true, creating: false });
    }
  };

  renderCreatingStorefront() {
    const { creating, createError } = this.state;

    return (
      <Modal
        title={createError && 'Something went wrong'}
        loadingTitle="Creating storefront, please wait..."
        loading={creating}
        onClose={this.onCloseCreatingModal}
        width={500}
        cancelText="Close"
      >
        {createError && (
          <p>
            An error occurred while creating your storefront.
            <br />
            Please try again, or contact support if the problem persists.
          </p>
        )}
      </Modal>
    );
  }

  onCloseCreatingModal = () => {
    this.setState({ showCreatingStorefront: false });
  };

  renderComingSoon() {
    return (
      <div className="storefront-apps-item-coming-soon">
        <StorefrontListItemCard
          image={<Icon fa="display-code" faType="light" />}
          title="More themes coming soon"
          subtitle="We're working to provide more theme options. Are you a developer? Help build the future of Swell by making your own themes."
          actionLabel="Read the docs"
          actionLink="https://developers.swell.is"
          isActionCard
        />
      </div>
    );
  }

  onChangeView = (_event, value) => {
    const { router, location } = this.props;
    const { isBrowseView } = this.state;

    if (value === 'browse' && isBrowseView) {
      return;
    }

    if (value === 'partner') {
      // Temporary until we have an internal partner listing
      window.open('https://www.swell.is/agencies', '_blank');
      router.replace(locationWithQuery(location, { view: 'browse' }, true));
      return;
    }

    const filters = value === 'browse' ? location.query.filters : undefined;

    router.replace(locationWithQuery(location, { view: value, filters }, true));
  };

  onClickUploadTheme = (event) => {
    event.preventDefault();
    const { router, location, onUploadTheme } = this.props;
    const { openModal } = this.context;

    openModal('UploadTheme', {
      onUpload: (result) => {
        if (result) {
          onUploadTheme(result);
          if (location.query.view !== 'library') {
            router.replace(
              locationWithQuery(
                location,
                { view: 'library', filters: undefined },
                true,
              ),
            );
          }
        }
      },
    });
  };

  isOwnApp = (app) => {
    return app.client_id === this.context.client.id;
  };

  isLibraryAppAvailable = (app) => {
    const { installedApps } = this.props;

    // Don't show storefront apps that require a theme
    if (app.storefront?.theme?.provider === 'app') {
      return false;
    }
    // Only show if the app is installed
    return Boolean(
      installedApps.results.find(
        (installedApp) => installedApp.app_id === app.id,
      ),
    );
  };

  isCatalogApp = (app) => {
    return (
      app.published &&
      (app.type === 'theme' || app.storefront?.theme?.provider !== 'app')
    );
  };

  getCatalogFilterFields(catalogItems) {
    const filters = CATALOG_FILTERS.reduce((acc, filter) => {
      const presentOptions = filter.options.filter((option) =>
        catalogItems.some((app) => app[filter.name]?.includes(option.id)),
      );
      acc.push({
        type: 'checkbox',
        ...filter,
        defaultValue: presentOptions[0]?.id
          ? [presentOptions[0].id]
          : [filter.options[0].id],
        value: this.state.filters?.[filter.name],
      });
      return acc;
    }, []);

    return filters;
  }

  onChangeCatalogForm = (values) => {
    const { router, location } = this.props;

    const filters = CATALOG_FILTERS.reduce((acc, filter) => {
      if (values[filter.name] !== undefined) {
        acc[filter.name] = values[filter.name];
      } else {
        acc[filter.name] = DEFAULT_CATALOG_FILTERS[filter.name];
      }
      return acc;
    }, {});

    const isDefaultFilters = isEqual(filters, DEFAULT_CATALOG_FILTERS);

    if (!isEqual(filters, this.state.filters) && !isDefaultFilters) {
      router.replace(locationWithQuery(location, { filters }, true));
    } else if (location.query.filters && isDefaultFilters) {
      // Remove filters from URL when using defaults
      router.replace(locationWithQuery(location, { filters: undefined }, true));
    }
  };

  render() {
    const {
      location: {
        query: { view, app: viewApp },
      },
      storefronts,
      submittedFindAPartner,
    } = this.props;

    const {
      loaded,
      loading,
      libraryItems,
      catalogItems,
      catalogFilterFields,
      hasOwnApps,
      isBrowseView,
      isBrowseOrLibraryView,
      showCreatingStorefront,
    } = this.state;

    if (!loaded) {
      return <ViewLoading />;
    }

    return (
      <>
        {submittedFindAPartner && <Confetti />}
        {viewApp && (
          <StorefrontNewAppView
            {...this.props}
            id={viewApp}
            createStorefront={this.createStorefront}
            onViewApp={this.onViewApp}
          />
        )}
        <Form
          onChange={this.onChangeCatalogForm}
          loading={loading}
          style={{ display: viewApp ? 'none' : undefined }}
        >
          <View
            uri="/storefronts"
            sectionTitle="Storefront"
            headerTitle="New storefront"
            headerSubtitle="Choose a storefront app or theme from our catalog, or build your own."
            detail={true}
            wide={false}
            headerActions={
              isBrowseOrLibraryView
                ? [
                    {
                      component: renderWithStorefrontLimitTooltip(
                        storefronts,
                        (limitReached) => (
                          <Button
                            type="secondary"
                            onClick={this.onClickUploadTheme}
                            disabled={limitReached}
                          >
                            <Icon fa="upload" />
                            Upload theme
                          </Button>
                        ),
                        { side: 'bottom' },
                      ),
                    },
                  ]
                : undefined
            }
            className={viewApp ? 'invisible' : undefined}
          >
            <div className="storefront-apps-container">
              <div className="storefront-apps-nav">
                <Field
                  name="view"
                  type="radio"
                  stacked
                  buttonsWithIcons
                  defaultValue={view || (hasOwnApps ? 'library' : 'browse')}
                  onChange={this.onChangeView}
                  options={[
                    ...(hasOwnApps
                      ? [
                          {
                            value: 'library',
                            label: 'My library',
                            icon: 'book-open-cover',
                          },
                        ]
                      : []),
                    {
                      value: 'browse',
                      label: 'Browse catalog',
                      icon: 'puzzle-piece',
                    },
                    {
                      value: 'build',
                      label: 'Build your own',
                      icon: 'square-terminal',
                    },
                    {
                      value: 'partner',
                      label: 'Find a partner',
                      icon: 'message-lines',
                    },
                  ].map(({ value, label, icon }) => ({
                    value,
                    label: (
                      <div className="form-radio-button-wrapper">
                        <div className="label">
                          <div className="form-button-icon">
                            <Icon fa={icon} />
                          </div>
                          {label}
                        </div>
                      </div>
                    ),
                  }))}
                />
                {isBrowseView && (
                  <>
                    {catalogFilterFields.map((field) => (
                      <Field
                        key={field.name}
                        stacked
                        {...field}
                        onChange={this.onChangeCatalogFilters}
                      />
                    ))}
                  </>
                )}
              </div>
              {view === 'library' ? (
                <div className="storefront-apps-list">
                  {libraryItems.map((app) => (
                    <StorefrontListItem
                      {...this.props}
                      key={app.id}
                      app={app}
                      inLibrary={true}
                      createStorefront={this.createStorefront}
                      fetchAppList={this.fetchAppList}
                    />
                  ))}
                </div>
              ) : view === 'build' ? (
                <StorefrontNewBuildYourOwn {...this.props} />
              ) : view === 'partner' ? (
                <StorefrontNewFindAPartner {...this.props} />
              ) : (
                <div className="storefront-apps-list">
                  {catalogItems.map((app) => (
                    <StorefrontListItem
                      {...this.props}
                      key={app.id}
                      app={app}
                      createStorefront={this.createStorefront}
                      onViewApp={this.onViewApp}
                    />
                  ))}
                  {this.renderComingSoon()}
                </div>
              )}
            </div>
          </View>
        </Form>
        {showCreatingStorefront && this.renderCreatingStorefront()}
      </>
    );
  }
}
